import { useQuery } from '@tanstack/react-query'
import { settingsService } from 'v2/shared/services/settings-service'
import { isSubAccountEmbedSession } from 'v2/shared/utils/embed-session'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { ConvChannels } from 'v2/types/api/conversations'
import { logger } from 'v2/utils/logger'

/**
 * Utility function to check if a flow is a default flow for any channel
 * This is a non-hook version for use in non-React contexts
 * @param flowId The ID of the flow to check
 * @returns Promise resolving to an object containing isDefaultFlow and defaultForChannels
 */
export const checkIsDefaultFlow = async (flowId: string) => {
  // Channel config is org-wide and hodor refuses it to sub-account embed sessions; the refusal
  // already read as "not default" through the catch below, so answer that without asking.
  if (isSubAccountEmbedSession()) {
    return { isDefaultFlow: false, defaultForChannels: [] as ConvChannels[] }
  }
  // Backend supports only these channels
  const channels = [ConvChannels.CALL, ConvChannels.MESSAGE, ConvChannels.WHATSAPP]

  const configs = await Promise.all(
    channels.map(async (channel) => {
      try {
        const response = await settingsService.getChannelConfiguration(channel.toLowerCase())
        return { channel, config: response.data }
      } catch (error) {
        logger.error('Error getting channel configuration', { error })
        return { channel, config: null }
      }
    }),
  )

  const defaultForChannels = configs.filter((item) => item.config?.outbound_phlo === flowId)

  return {
    isDefaultFlow: defaultForChannels.length > 0,
    defaultForChannels: defaultForChannels.map((item) => item.channel),
  }
}

/**
 * Hook to check if a flow is set as the default flow for any channel
 * @param flowId The ID of the flow to check
 * @returns Object containing isDefaultFlow, defaultForChannels, and isLoading
 */
export const useDefaultFlowCheck = (flowId?: string) => {
  const { data, isLoading } = useQuery({
    queryKey: flowQueryKeys().channelConfigs(flowId),
    queryFn: async () => {
      if (!flowId) return { isDefaultFlow: false, defaultForChannels: [] }
      return checkIsDefaultFlow(flowId)
    },
    enabled: !!flowId,
  })

  return {
    isDefaultFlow: data?.isDefaultFlow || false,
    defaultForChannels: data?.defaultForChannels || [],
    isLoading,
  }
}
